import type { WithPagination } from "../types/types";
import { makeUrlWithParams } from "./util";

function searchParamsToRecord(
  params: URLSearchParams
): Record<string, string | string[]> {
  const record: Record<string, string | string[]> = {};
  for (const [key, value] of params.entries()) {
    const existing = record[key];
    if (existing === undefined) {
      record[key] = value;
    } else if (Array.isArray(existing)) {
      existing.push(value);
    } else {
      record[key] = [existing, value];
    }
  }
  return record;
}

/**
 * Builds a paginated, offset/limit-based request URL for `useSWRInfinite`
 * key functions. Shared by every infinite-scroll list in the app so offset
 * math and query-string building live in one place.
 *
 * Callers that need isolated SWR cache entries for the same URL (e.g. two
 * select fields hitting one list endpoint) should return a tuple key
 * `[url, cacheScope]` from their key function and pass a fetcher that only
 * uses the URL portion.
 */
export function constructInfiniteScrollQuery<T>({
  baseUrl,
  existingParams,
  query,
  previousPageData,
  index,
  pageSize = 20,
}: {
  baseUrl: string;
  existingParams?: URLSearchParams;
  query: string;
  previousPageData: WithPagination<{ data: T[] }> | null;
  index: number;
  pageSize?: number;
}): string | null {
  if (previousPageData && previousPageData.data.length === 0) {
    return null;
  }

  const offset =
    index === 0 ? 0 : (previousPageData?.pagination.offset ?? 0) + pageSize;

  const requestUrl = makeUrlWithParams(baseUrl, {
    offset,
    limit: pageSize,
    ...(existingParams ? searchParamsToRecord(existingParams) : {}),
    ...(query ? { q: query } : {}),
  });

  return requestUrl;
}
